import { IncidentChannelValue, IncidentPriorityValue } from '@data';
import { calendarDay } from '@app/utils/helpers/date-utils';

import { instantOn } from './incidentLength';

/** The option that stands for "it is not in the list, because nobody has logged it". */
export const NEW_INCIDENT = 'new';

/** An incident logged by whoever worked on it, as the form holds it. Nothing a manager decides is here. */
export interface NewIncidentDraft {
  title: string;
  /** Optional: an incident is real without a write-up, and its own page is where one gets written. */
  description: string;
  priority: IncidentPriorityValue | '';
  reportedDay: Date;
  reportedTime: string;
  /** Null means "the day it came in", which is the answer nearly every time. */
  answeredDay: Date | null;
  answeredTime: string;
}

/** Only the day arrives filled in, and only because today is overwhelmingly the likely one. */
export const emptyNewIncident = (day: Date = calendarDay(new Date())): NewIncidentDraft => ({
  title: '',
  description: '',
  priority: '',
  reportedDay: day,
  reportedTime: '',
  answeredDay: null,
  answeredTime: '',
});

/** When it came in, or nothing while the hour is still unanswered. */
export const newIncidentReportedAt = (draft: NewIncidentDraft): Date | null =>
  draft.reportedTime ? instantOn(draft.reportedDay, draft.reportedTime) : null;

/** When somebody first got back to whoever raised it, on its own day or the day it came in. */
export const newIncidentAnsweredAt = (draft: NewIncidentDraft): Date | null =>
  draft.answeredTime ? instantOn(draft.answeredDay ?? draft.reportedDay, draft.answeredTime) : null;

/** Whether every question an incident cannot be written without has an answer. */
export const isNewIncidentAnswered = (draft: NewIncidentDraft): boolean =>
  !!draft.title.trim() && !!draft.priority && !!draft.reportedTime;

/** What is wrong with an answer that was given, never the omission: the picker still allows an earlier hour that day. */
export const newIncidentError = (draft: NewIncidentDraft, now: Date = new Date()): string | undefined => {
  const answeredAt = newIncidentAnsweredAt(draft);
  if (!answeredAt) return undefined;

  const reportedAt = newIncidentReportedAt(draft);
  if (reportedAt && answeredAt.getTime() < reportedAt.getTime()) return 'incidents.error.beforeReported';

  return answeredAt.getTime() > now.getTime() ? 'reportIncident.answeredInFuture' : undefined;
};

/** The draft as the create endpoint takes it. `channel` says it was logged from Phill itself. */
export const newIncidentValues = (
  draft: NewIncidentDraft,
): {
  title: string;
  description: string;
  priority: IncidentPriorityValue;
  channel: IncidentChannelValue;
  reportedAt: string;
  respondedAt?: string;
} => {
  const answeredAt = newIncidentAnsweredAt(draft);

  return {
    title: draft.title.trim(),
    // Empty is a real answer: an incident is logged the minute it comes in, before anyone writes it up.
    description: draft.description.trim(),
    priority: draft.priority as IncidentPriorityValue,
    channel: 'phill' as IncidentChannelValue,
    reportedAt: (newIncidentReportedAt(draft) as Date).toISOString(),
    // Without the hour the response target reads as never answered, which is a breach the record cannot undo.
    ...(answeredAt ? { respondedAt: answeredAt.toISOString() } : {}),
  };
};
